/**
 * The per-ticket rollup stored at `workflow.samples` on the ticket's mirror doc (PRD §9.10), and the
 * sample phases the ticket list filters on. The rollup holds ONLY time-independent facts (counts and
 * the oldest/newest timestamps); every time-based verdict is computed when read, so a stored rollup
 * cannot go stale as days pass. The server writes it; the client only reads it.
 */
import { attentionFlags, worstFlag, DEFAULT_THRESHOLDS, type AttentionFlag, type AttentionInput, type AttentionThresholds } from './attention';
import type { SampleStatus } from './lifecycle';

export interface TicketSampleRollup {
  readonly count: number;
  readonly expected: number;
  readonly logged: number;
  readonly sent: number;
  readonly resulted: number;
  readonly closed: number;
  readonly lost: number;
  readonly cancelled: number;
  readonly checkFsr: number;
  readonly needsTicket: number;
  readonly actionRequired: number;
  readonly oldestExpectedAt: number | null;
  readonly oldestUnshippedAt: number | null;
  readonly oldestSentAt: number | null;
  /** Newest `lostAt` among lost samples; null when none is lost OR any lost sample is undated. */
  readonly newestLostAt: number | null;
}

const oldest = (times: ReadonlyArray<number | null>): number | null => {
  const known = times.filter((t): t is number => t !== null);
  return known.length > 0 ? Math.min(...known) : null;
};

// An undated loss never ages out (matching attentionFlags), so it pins the newest loss to null.
const newestLoss = (times: ReadonlyArray<number | null>): number | null =>
  times.length === 0 || times.includes(null) ? null : Math.max(...(times as readonly number[]));

export function computeTicketSampleRollup(samples: readonly AttentionInput[]): TicketSampleRollup {
  const countOf = (status: SampleStatus): number => samples.filter((s) => s.status === status).length;
  // Standing (time-independent) conditions: reuse the per-sample rules at a "now" of -Infinity,
  // where no clock-based flag can fire, so only check_fsr / needs_ticket / action_required remain.
  const standing = samples.map((s) => attentionFlags(s, Number.NEGATIVE_INFINITY));
  const withFlag = (flag: AttentionFlag): number => standing.filter((f) => f.includes(flag)).length;
  return {
    count: samples.length,
    expected: countOf('expected'),
    logged: countOf('logged'),
    sent: countOf('sent'),
    resulted: countOf('resulted'),
    closed: countOf('closed'),
    lost: countOf('lost'),
    cancelled: countOf('cancelled'),
    checkFsr: withFlag('check_fsr'),
    needsTicket: withFlag('needs_ticket'),
    actionRequired: withFlag('action_required'),
    oldestExpectedAt: oldest(samples.filter((s) => s.status === 'expected' && !s.historical).map((s) => s.collectedAt)),
    oldestUnshippedAt: oldest(samples.filter((s) => s.status === 'logged' && s.shipmentId === null).map((s) => s.loggedAt)),
    oldestSentAt: oldest(samples.filter((s) => s.status === 'sent').map((s) => s.sentAt)),
    newestLostAt: newestLoss(samples.filter((s) => s.status === 'lost').map((s) => s.lostAt)),
  };
}

const DAY = 86_400_000;
const past = (at: number | null, days: number, now: number): boolean => at !== null && now - at > days * DAY;

export function rollupWorstFlag(r: TicketSampleRollup, now: number, t: AttentionThresholds = DEFAULT_THRESHOLDS): AttentionFlag | null {
  const candidates: ReadonlyArray<readonly [boolean, AttentionFlag]> = [
    [r.checkFsr > 0, 'check_fsr'],
    [r.needsTicket > 0, 'needs_ticket'],
    [past(r.oldestExpectedAt, t.bottleArrivalDays, now), 'bottle_not_received'],
    [past(r.oldestUnshippedAt, t.notShippedDays, now), 'not_shipped'],
    [past(r.oldestSentAt, t.expectedTurnaroundDays, now), 'overdue_at_lab'],
    [r.resulted > 0, 'new_report'],
    [r.actionRequired > 0, 'action_required'],
    [r.lost > 0 && !past(r.newestLostAt, t.lostWindowDays, now), 'lost'],
  ];
  return worstFlag(candidates.filter(([on]) => on).map(([, flag]) => flag));
}

export type TicketSamplePhase = 'collected_not_sent' | 'at_lab' | 'report_received' | 'closed' | 'needs_attention';

export const TICKET_SAMPLE_PHASES: readonly TicketSamplePhase[] = [
  'collected_not_sent', 'at_lab', 'report_received', 'closed', 'needs_attention',
];

export const TICKET_SAMPLE_PHASE_LABELS: Readonly<Record<TicketSamplePhase, string>> = {
  collected_not_sent: 'Collected, not sent',
  at_lab: 'Sent, waiting on report',
  report_received: 'Report received',
  closed: 'Reviewed / closed',
  needs_attention: 'Needs attention',
};

export function ticketSamplePhases(r: TicketSampleRollup | null | undefined, now: number, t: AttentionThresholds = DEFAULT_THRESHOLDS): TicketSamplePhase[] {
  if (!r || r.count === 0) return [];
  const phases: ReadonlyArray<readonly [boolean, TicketSamplePhase]> = [
    [r.expected + r.logged > 0, 'collected_not_sent'],
    [r.sent > 0, 'at_lab'],
    [r.resulted > 0, 'report_received'],
    [r.closed > 0, 'closed'],
    [rollupWorstFlag(r, now, t) !== null, 'needs_attention'],
  ];
  return phases.filter(([on]) => on).map(([, phase]) => phase);
}
